import { TFunction } from 'i18next'

import { GiftCardTopupRecord } from '@/api/rewards/get-gift-card-topups'
import { ApproveDeclineButtons } from '@/components/ui/approve-decline-buttons'
import { Badge } from '@/components/ui/badge'
import { BadgeColorKeys, BadgeVariantKeys } from '@/components/ui/badge/badge.types'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import {
  TopupStatus,
  TopupStatusEnum,
  TopupType,
  TopupTypeEnum,
} from '@/models/features/communication'
import { UserRoleEnum } from '@/models/features/user.models'
import { formatDateOnly, formatTimeOnly } from '@/utils/date'
import { getUserRoleLabel } from '@/utils/user'

import * as S from './financials.styled'
import { FinancialsTableFilters, GetColumnsOptions } from './financials.types'

export type { TopupStatus, TopupType }
export { TopupStatusEnum, TopupTypeEnum }

export const getCsvFields = (t: TFunction<['communication']>) => [
  {
    label: t('communication:financials.columns.dateTime'),
    value: (row: GiftCardTopupRecord) =>
      `${formatDateOnly(new Date(row.createdAt))} ${formatTimeOnly(new Date(row.createdAt))}`,
  },
  {
    label: t('communication:financials.columns.transactionId'),
    value: 'id',
  },
  {
    label: t('communication:financials.columns.transactionType'),
    value: (row: GiftCardTopupRecord) => getTransactionTypeLabel(row.type, t),
  },
  {
    label: t('communication:financials.columns.triggeredBy'),
    value: (row: GiftCardTopupRecord) =>
      `${getUserRoleLabel(row.byRoleName)} - ${row.userFullName}`,
  },
  {
    label: t('communication:financials.columns.building'),
    value: 'buildingName',
  },
  {
    label: t('communication:financials.columns.amount'),
    value: (row: GiftCardTopupRecord) => `+$${row.amount}`,
  },
  {
    label: t('communication:financials.columns.status'),
    value: (row: GiftCardTopupRecord) => row.status.charAt(0).toUpperCase() + row.status.slice(1),
  },
]

export const getTransactionTypeLabel = (type: TopupType, t: TFunction<['communication']>) => {
  const TRANSACTION_TYPE_LABEL_MAP: Record<TopupType, string> = {
    [TopupTypeEnum.enum.card]: t('communication:financials.transactionType.options.card'),
    [TopupTypeEnum.enum.invoice]: t('communication:financials.transactionType.options.invoice'),
  }

  return TRANSACTION_TYPE_LABEL_MAP[type] ?? type
}

type StatusBadgeConfig = { color: BadgeColorKeys; variant: BadgeVariantKeys }

export const STATUS_COLOR_MAP: Record<TopupStatus, StatusBadgeConfig> = {
  [TopupStatusEnum.enum.approved]: { color: 'green', variant: 'light' },
  [TopupStatusEnum.enum.declined]: { color: 'red', variant: 'light' },
  [TopupStatusEnum.enum.pending]: { color: 'orange', variant: 'solid' },
}

export const getStatusBadgeConfig = (status: TopupStatus): StatusBadgeConfig =>
  STATUS_COLOR_MAP[status]

export const financialsTableInitialFilters: FinancialsTableFilters = {
  buildingId: undefined,
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  toDate: undefined,
}

const getBaseColumns = (t: TFunction<['communication']>): TableColumn<GiftCardTopupRecord>[] => [
  {
    id: 'dateTime',
    render: (row) => (
      <div>
        <Typography color="black800" size="text-small" weight="medium">
          {formatDateOnly(new Date(row.createdAt))}
        </Typography>
        <Typography color="black500" size="text-small" weight="regular">
          {formatTimeOnly(new Date(row.createdAt))}
        </Typography>
      </div>
    ),
    title: t('communication:financials.columns.dateTime'),
    width: '12rem',
  },
  {
    id: 'transactionType',
    render: (row) => (
      <Typography color="black800" size="text-small" weight="regular">
        {getTransactionTypeLabel(row.type, t)}
      </Typography>
    ),
    title: t('communication:financials.columns.transactionType'),
    width: '18rem',
  },
  {
    id: 'triggeredBy',
    render: (row) => (
      <div>
        <Typography color="black800" size="text-small" weight="medium">
          {getUserRoleLabel(row.byRoleName)}
        </Typography>
        <Typography color="black500" size="text-small" weight="regular" breakLines={1}>
          {row.userFullName}
        </Typography>
      </div>
    ),
    title: t('communication:financials.columns.triggeredBy'),
    width: '16rem',
  },
  {
    id: 'building',
    render: (row) => (
      <S.BuildingName color="black800" size="text-small" weight="regular">
        {row.buildingName}
      </S.BuildingName>
    ),
    title: t('communication:financials.columns.building'),
    width: '18rem',
  },
  {
    id: 'amount',
    render: (row) => (
      <Typography color="green500" size="text-small" weight="medium">
        +${row.amount}
      </Typography>
    ),
    title: t('communication:financials.columns.amount'),
    width: '10rem',
  },
  {
    id: 'status',
    render: (row) => (
      <Badge {...getStatusBadgeConfig(row.status)} size="small">
        {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
      </Badge>
    ),
    title: t('communication:financials.columns.status'),
    width: '12rem',
  },
]

const getActionsColumn = (
  { approvingIds, decliningIds, onApprove, onDecline }: Omit<GetColumnsOptions, 'userRole'>,
  t: TFunction<['communication']>,
): TableColumn<GiftCardTopupRecord> => ({
  id: 'actions',
  render: (row) => {
    if (row.status !== TopupStatusEnum.enum.pending || row.type !== TopupTypeEnum.enum.invoice) {
      return null
    }
    return (
      <ApproveDeclineButtons
        approveDisabled={decliningIds.has(row.id)}
        approveLoading={approvingIds.has(row.id)}
        declineDisabled={approvingIds.has(row.id)}
        declineLoading={decliningIds.has(row.id)}
        onApprove={() => onApprove(row)}
        onDecline={() => onDecline(row)}
      />
    )
  },
  title: t('communication:financials.columns.actions'),
  width: '12rem',
})

export const getFinancialsTableColumns = (
  t: TFunction<['communication']>,
  { userRole, approvingIds, decliningIds, onApprove, onDecline }: GetColumnsOptions,
): TableColumn<GiftCardTopupRecord>[] => {
  const baseColumns = getBaseColumns(t)
  if (userRole !== UserRoleEnum.enum.Administrator) return baseColumns
  return [...baseColumns, getActionsColumn({ approvingIds, decliningIds, onApprove, onDecline }, t)]
}
